import { Button, Header, ScrollContainer, StickyFooterButtons, useLoading } from "best-common-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { getReport, runReport } from "../api/RequesTixApi";
import ReportHandler from "../components/reports/ReportHandler";
import RouteConstants from "../constants/RouteConstants";
import { useAuth } from "../contexts/AuthContext";
import { ReportDataDTO, Reports } from "../types/Reports";
import { downloadLink, FileTypes } from "../util/FileUtil";
import { useUrlParams } from "../util/RouteUtil";

type ReportUrlProps = {
  reportId: string;
};

const REPORT_PARAM_DEFAULTS = {
  search: "",
  seasonId: undefined,
  categoryId: undefined,
  fulfillmentTypeId: undefined,
  statusId: undefined,
  userId: undefined,
  departmentId: undefined,
  projectId: undefined,
  gameSearchId: undefined,
  startDate: undefined,
  endDate: undefined,
  homeTeamId: undefined,
} as const;

const ReportsContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reportId } = useParams<ReportUrlProps>();
  const { userInfo, isAdmin } = useAuth();
  const { setLoading } = useLoading();
  const [report, setReport] = useState<Reports>();
  const [canExport, setCanExport] = useState(true);

  const [params, setParam] = useUrlParams({ ...REPORT_PARAM_DEFAULTS });

  const reportData: ReportDataDTO = {
    search: params.search ?? "",
    seasonId: params.seasonId ? Number(params.seasonId) : undefined,
    categoryId: params.categoryId ? Number(params.categoryId) : undefined,
    fulfillmentTypeId: params.fulfillmentTypeId ? Number(params.fulfillmentTypeId) : undefined,
    statusId: params.statusId ? Number(params.statusId) : undefined,
    userId: params.userId ?? undefined,
    departmentId: params.departmentId ? Number(params.departmentId) : undefined,
    projectId: params.projectId ? Number(params.projectId) : undefined,
    gameSearchId: params.gameSearchId ? Number(params.gameSearchId) : undefined,
    startDate: params.startDate ?? undefined,
    endDate: params.endDate ?? undefined,
    homeTeamId: params.homeTeamId ? Number(params.homeTeamId) : undefined,
  };

  const exportReport = async () => {
    try {
      setLoading(true);
      const response = await runReport(reportId, reportData);
      downloadLink(response, FileTypes.EXCEL);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const setReportData = useCallback(
    (reportDataSetStateAction: React.SetStateAction<ReportDataDTO>) => {
      const newReportData =
        typeof reportDataSetStateAction === "function"
          ? reportDataSetStateAction(reportData)
          : reportDataSetStateAction;
      const resolvedUserId = isAdmin ? newReportData.userId : userInfo!.employeeUserId;
      const updates: Record<string, string | undefined> = {
        search: newReportData.search || undefined,
        seasonId: newReportData.seasonId != null ? String(newReportData.seasonId) : undefined,
        categoryId: newReportData.categoryId != null ? String(newReportData.categoryId) : undefined,
        fulfillmentTypeId:
          newReportData.fulfillmentTypeId != null ? String(newReportData.fulfillmentTypeId) : undefined,
        statusId: newReportData.statusId != null ? String(newReportData.statusId) : undefined,
        userId: resolvedUserId != null ? String(resolvedUserId) : undefined,
        departmentId: newReportData.departmentId != null ? String(newReportData.departmentId) : undefined,
        projectId: newReportData.projectId != null ? String(newReportData.projectId) : undefined,
        startDate: newReportData.startDate ?? undefined,
        endDate: newReportData.endDate ?? undefined,
        homeTeamId: newReportData.homeTeamId != null ? String(newReportData.homeTeamId) : undefined,
        gameSearchId: newReportData.gameSearchId != null ? String(newReportData.gameSearchId) : undefined,
      };
      setParam(updates);
    },
    [isAdmin, userInfo, setParam, reportData],
  );

  useEffect(() => {
    if (reportId) {
      getReport(reportId)
        .then(setReport)
        .catch(() => {
          navigate(RouteConstants.BASE);
        });
    }
  }, [reportId]);

  useEffect(() => {
    if (!params.userId && userInfo) {
      setReportData({ ...reportData, userId: userInfo.employeeUserId });
    }
  }, [userInfo]);

  useEffect(() => {
    if (!params.userId && !isAdmin && userInfo) {
      setReportData({ ...reportData, userId: userInfo.employeeUserId });
    }
  }, [params.userId, userInfo]);

  return (
    <div>
      <Header
        sticky
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div>{report?.reportName ?? ""}</div>
            <StickyFooterButtons startsOpen={true}>
              <Button variant="default" onClick={exportReport} disabled={!canExport}>
                Export
              </Button>
            </StickyFooterButtons>
          </div>
        }
      />
      <ScrollContainer className="bcr-p-2">
        <div className="bcr-mb-2">
          {!!report && (
            <ReportHandler
              report={report}
              reportData={reportData}
              setReportData={setReportData}
              location={location}
              setCanExport={setCanExport}
            />
          )}
        </div>
      </ScrollContainer>
    </div>
  );
};

export default ReportsContainer;
